import { secureGet, securePost, securePut, secureDelete } from '../utils/HttpUtils';
import { EconomicGroup } from '../types/interfaces';

const apiUrls = {
  economicGroups: '/v1/private/economic-groups',
};

const logEndpointInfo = (method: string, url: string, data?: any, response?: any, error?: any) => {
  console.group(`[EconomicGroupService] ${method} ${url}`);
  if (data) console.log('Request Body:', data);
  if (response) console.log('Response:', response);
  if (error) console.error('Error:', error);
  console.groupEnd();
};

export const getEconomicGroups = async (): Promise<{
  success: boolean;
  data?: EconomicGroup[];
  message?: string;
  status?: number;
}> => {
  try {
    const response = await secureGet(apiUrls.economicGroups);
    logEndpointInfo('GET', apiUrls.economicGroups, undefined, response);
    if (response.success && 'data' in response) {
      return { success: true, data: response.data as EconomicGroup[] };
    }
    return { success: false, message: 'Error al obtener los grupos económicos', status: response.status };
  } catch (error) {
    console.error('[EconomicGroupService] GET - Error:', error);
    return { success: false, message: 'Error al obtener los grupos económicos' };
  }
};

export const createEconomicGroup = async (
  name: string,
  code: string,
): Promise<{
  success: boolean;
  data?: EconomicGroup;
  message?: string;
  status?: number;
}> => {
  try {
    const body = { name, code };
    const response = await securePost(apiUrls.economicGroups, body);
    logEndpointInfo('POST', apiUrls.economicGroups, body, response);
    if (response.success && 'data' in response) {
      return { success: true, data: response.data as EconomicGroup };
    }
    if (response.status === 404 || response.status === 405 || response.status === 501) {
      return {
        success: false,
        message: 'La funcionalidad de crear grupos económicos aún no está disponible en el servidor',
        status: response.status,
      };
    }
    return { success: false, message: response.message || 'Error al crear el grupo económico', status: response.status };
  } catch (error: any) {
    if (error?.response?.status === 404 || error?.response?.status === 405 || error?.response?.status === 501) {
      return {
        success: false,
        message: 'La funcionalidad de crear grupos económicos aún no está disponible en el servidor',
        status: error.response?.status,
      };
    }
    return { success: false, message: 'Error al crear el grupo económico' };
  }
};

export const updateEconomicGroup = async (
  id: number,
  name: string,
  code: string,
): Promise<{
  success: boolean;
  data?: EconomicGroup;
  message?: string;
  status?: number;
}> => {
  try {
    const url = `${apiUrls.economicGroups}/${id}`;
    const body = { name, code };
    const response = await securePut(url, body);
    logEndpointInfo('PUT', url, body, response);
    if (response.success && 'data' in response) {
      return { success: true, data: response.data as EconomicGroup };
    }
    if (response.status === 404 || response.status === 405 || response.status === 501) {
      return {
        success: false,
        message: 'La funcionalidad de editar grupos económicos aún no está disponible en el servidor',
        status: response.status,
      };
    }
    return { success: false, message: response.message || 'Error al actualizar el grupo económico', status: response.status };
  } catch (error: any) {
    if (error?.response?.status === 404 || error?.response?.status === 405 || error?.response?.status === 501) {
      return {
        success: false,
        message: 'La funcionalidad de editar grupos económicos aún no está disponible en el servidor',
        status: error.response?.status,
      };
    }
    return { success: false, message: 'Error al actualizar el grupo económico' };
  }
};

export const deleteEconomicGroup = async (
  id: number,
): Promise<{
  success: boolean;
  message?: string;
  status?: number;
}> => {
  try {
    const url = `${apiUrls.economicGroups}/${id}`;
    const response = await secureDelete(url);
    logEndpointInfo('DELETE', url, { id }, response);
    if (response.success) {
      return { success: true };
    }
    if (response.status === 404 || response.status === 405 || response.status === 501) {
      return {
        success: false,
        message: 'La funcionalidad de eliminar grupos económicos aún no está disponible en el servidor',
        status: response.status,
      };
    }
    return { success: false, message: response.message || 'Error al eliminar el grupo económico', status: response.status };
  } catch (error: any) {
    if (error?.response?.status === 404 || error?.response?.status === 405 || error?.response?.status === 501) {
      return {
        success: false,
        message: 'La funcionalidad de eliminar grupos económicos aún no está disponible en el servidor',
        status: error.response?.status,
      };
    }
    return { success: false, message: 'Error al eliminar el grupo económico' };
  }
};
